import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getOutlookConnectUrl, getOutlookStatus, disconnectOutlook, triggerOutlookSync, getOutlookDiagnostics } from '../api';

function IntegrationsSettings({ authAxios, showFormMessage, userRole }) {
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const canEdit = useMemo(() => ['PARTNER', 'SUPERUSER', 'ASSOCIATE', 'PARALEGAL', 'SUPPORT_STAFF', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getOutlookStatus(authAxios);
      setOutlookStatus(data);
    } catch (err) {
      console.error('Error fetching Outlook status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Check for OAuth callback result in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('outlook_connected') === '1') {
      showFormMessage('success', 'Outlook account connected successfully!');
      fetchStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('outlook_error')) {
      showFormMessage('error', `Outlook connection failed: ${params.get('outlook_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [showFormMessage, fetchStatus]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const data = await getOutlookConnectUrl(authAxios);
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        showFormMessage('error', 'Outlook integration is not configured. Please contact your administrator.');
        setIsConnecting(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to initiate Outlook connection.';
      showFormMessage('error', msg);
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setShowDisconnectModal(false);
    setIsDisconnecting(true);
    try {
      await disconnectOutlook(authAxios);
      showFormMessage('success', 'Outlook account disconnected.');
      fetchStatus();
    } catch (err) {
      showFormMessage('error', 'Failed to disconnect Outlook.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleSync = async (type) => {
    setIsSyncing(true);
    try {
      const data = await triggerOutlookSync(authAxios, type);
      const detail = data?.detail || `Outlook ${type} sync started.`;
      showFormMessage('success', detail);
    } catch (err) {
      const msg = err?.response?.data?.detail || `Failed to start ${type} sync.`;
      showFormMessage('error', msg);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDiagnose = async () => {
    setIsDiagnosing(true);
    setDiagnostics(null);
    try {
      const data = await getOutlookDiagnostics(authAxios);
      setDiagnostics(data);
      if (data.error) {
        showFormMessage('error', data.error);
      } else {
        showFormMessage('success', 'Diagnostics complete — connection is healthy.');
      }
    } catch (err) {
      showFormMessage('error', 'Failed to run diagnostics.');
    } finally {
      setIsDiagnosing(false);
    }
  };

  if (isLoading) {
    return <div className="p-6 text-gray-600">Loading Integration Settings...</div>;
  }

  const connected = outlookStatus?.connected;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Integrations</h2>
      </div>

      {/* Outlook Connection Card */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <svg className="w-8 h-8 text-blue-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21.17 2.06A1.5 1.5 0 0 0 19.74 1.5H4.26a1.5 1.5 0 0 0-1.43.56A1.5 1.5 0 0 0 2.5 3.5v17a1.5 1.5 0 0 0 .33 1.44 1.5 1.5 0 0 0 1.43.56h15.48a1.5 1.5 0 0 0 1.43-.56 1.5 1.5 0 0 0 .33-1.44v-17a1.5 1.5 0 0 0-.33-1.44zM12 17.5l-7-5 7-5 7 5-7 5z"/>
          </svg>
          <h3 className="text-lg font-semibold text-primary">Microsoft Outlook</h3>
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${connected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
            {connected ? 'Connected' : 'Not Connected'}
          </span>
        </div>

        {connected ? (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-sm min-w-0">
              <div className="min-w-0">
                <span className="text-gray-500 block">Connected Account:</span>
                <p className="font-medium truncate" title={outlookStatus.outlook_email || '—'}>{outlookStatus.outlook_email || '—'}</p>
              </div>
              <div className="min-w-0">
                <span className="text-gray-500 block">Connected Since:</span>
                <p className="font-medium truncate">{outlookStatus.connected_at ? new Date(outlookStatus.connected_at).toLocaleDateString() : '—'}</p>
              </div>
              <div className="min-w-0">
                <span className="text-gray-500 block">Last Email Sync:</span>
                <p className="font-medium truncate">{outlookStatus.last_mail_sync ? new Date(outlookStatus.last_mail_sync).toLocaleString() : 'Never'}</p>
              </div>
              <div className="min-w-0">
                <span className="text-gray-500 block">Last Calendar Sync:</span>
                <p className="font-medium truncate">{outlookStatus.last_calendar_sync ? new Date(outlookStatus.last_calendar_sync).toLocaleString() : 'Never'}</p>
              </div>
            </div>
            {outlookStatus.is_token_expired && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded mb-4 text-sm">
                Your Outlook session has expired. Please reconnect to resume syncing.
              </div>
            )}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              <button onClick={() => handleSync('email')} disabled={isSyncing} className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
                {isSyncing ? 'Syncing...' : 'Sync Emails Now'}
              </button>
              <button onClick={() => handleSync('calendar')} disabled={isSyncing} className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
                {isSyncing ? 'Syncing...' : 'Sync Calendar Now'}
              </button>
              <button onClick={handleDiagnose} disabled={isDiagnosing} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded border border-gray-300 hover:bg-gray-200 disabled:opacity-50">
                {isDiagnosing ? 'Diagnosing...' : 'Diagnose Connection'}
              </button>
              <button onClick={() => setShowDisconnectModal(true)} disabled={isDisconnecting} className="ml-auto px-4 py-2 bg-red-50 text-red-700 text-sm rounded border border-red-200 hover:bg-red-100 disabled:opacity-50">
                {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>

            {/* Disconnect Confirmation Modal */}
            {showDisconnectModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Disconnect Outlook?</h3>
                  <p className="text-sm text-gray-600 mb-6">Are you sure you want to disconnect your Outlook account? Email and calendar syncing will stop.</p>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => setShowDisconnectModal(false)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDisconnect}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Diagnostics Results */}
            {diagnostics && (
              <div className="mt-4 p-4 bg-gray-50 rounded border border-gray-200 text-sm">
                <h4 className="font-semibold text-gray-800 mb-2">Diagnostics Results</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><span className="text-gray-500">Token Valid:</span> <span className={diagnostics.token_valid ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>{diagnostics.token_valid ? 'Yes' : 'No'}</span></div>
                  <div><span className="text-gray-500">Graph /me OK:</span> <span className={diagnostics.me_endpoint_ok ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>{diagnostics.me_endpoint_ok ? 'Yes' : 'No'}</span></div>
                  <div><span className="text-gray-500">Mail Endpoint:</span> <span className={diagnostics.mail_endpoint_ok ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>{diagnostics.mail_endpoint_ok ? `OK (${diagnostics.mail_test_count} test msg)` : 'FAILED'}</span></div>
                  <div><span className="text-gray-500">Calendar Endpoint:</span> <span className={diagnostics.calendar_endpoint_ok ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>{diagnostics.calendar_endpoint_ok ? `OK (${diagnostics.calendar_test_count} test evt)` : 'FAILED'}</span></div>
                  <div><span className="text-gray-500">Credential Source:</span> <span className={diagnostics.credential_source === 'global/key-vault' ? 'text-green-700 font-medium' : 'text-yellow-700 font-medium'}>{diagnostics.credential_source || '—'}</span></div>
                  <div><span className="text-gray-500">Client ID:</span> <span className="font-medium font-mono text-xs">{diagnostics.client_id_prefix || '—'}</span></div>
                  <div><span className="text-gray-500">Tenant ID:</span> <span className={diagnostics.tenant_id === 'common' ? 'text-green-700 font-medium' : 'text-yellow-700 font-medium'}>{diagnostics.tenant_id || '—'}</span></div>
                  <div><span className="text-gray-500">Graph Email:</span> <span className="font-medium break-all">{diagnostics.graph_email || '—'}</span></div>
                  <div><span className="text-gray-500">Token Expires In:</span> <span className="font-medium">{diagnostics.expires_in_minutes != null ? `${diagnostics.expires_in_minutes} min` : '—'}</span></div>
                  <div className="sm:col-span-2"><span className="text-gray-500">Scopes Granted:</span> <span className="font-medium break-all">{diagnostics.scopes_granted || '(none)'}</span></div>
                  <div><span className="text-gray-500">Mail Delta Link:</span> <span className="font-medium">{diagnostics.mail_delta_link ? 'Present' : 'None'}</span></div>
                  <div><span className="text-gray-500">Calendar Delta Link:</span> <span className="font-medium">{diagnostics.calendar_delta_link ? 'Present' : 'None'}</span></div>
                  {diagnostics.mail_error && (
                    <div className="sm:col-span-2 mt-1 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-xs break-all"><strong>Mail Error:</strong> {diagnostics.mail_error}</div>
                  )}
                  {diagnostics.calendar_error && (
                    <div className="sm:col-span-2 mt-1 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-xs break-all"><strong>Calendar Error:</strong> {diagnostics.calendar_error}</div>
                  )}
                  {diagnostics.guidance && (
                    <div className="sm:col-span-2 mt-1 p-2 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded">{diagnostics.guidance}</div>
                  )}
                  {diagnostics.error && !diagnostics.guidance && (
                    <div className="sm:col-span-2 mt-1 p-2 bg-red-50 border border-red-200 text-red-700 rounded">{diagnostics.error}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Connect your Microsoft Outlook account to sync emails and calendar events with LegalOps.
              Emails can be automatically linked to matters, and calendar events sync bidirectionally.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              You will be redirected to Microsoft to sign in and grant permissions.
              Permissions requested: read/send email, read/write calendar, read contacts.
            </p>
            {canEdit && (
              <button onClick={handleConnect} disabled={isConnecting} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded shadow hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {isConnecting ? 'Redirecting...' : 'Connect Outlook Account'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Features explanation */}
      <div className="bg-white p-6 rounded shadow">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">What gets synced?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-gray-800 mb-2">Email</h4>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Incoming emails appear in your Unlinked Inbox</li>
              <li>Auto-link rules can file emails to matters automatically</li>
              <li>Compose and send emails directly from LegalOps</li>
              <li>File emails and attachments to case documents</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-2">Calendar</h4>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Hearings, deadlines, and meetings sync to Outlook</li>
              <li>Outlook events sync back to LegalOps calendar</li>
              <li>Conflict detection for simultaneous edits</li>
              <li>Task due dates appear as Outlook events</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IntegrationsSettings;
